import type { CSSProperties } from 'react';
import type { PortalBrandTheme } from '../../types/portalReport.js';

// CLIENT PORTAL Phase 2 — a theme, as inline CSS variables on the portal root.
//
// ⚠️ Inline style rather than a class per brand, because the brand set is data:
// a new brand is a config entry, and generating a stylesheet rule per brand at
// build time would make it a code change again.
//
// ⚠️⚠️ THE VARIABLES THIS EMITS ARE THE COMPLETE LIST OF WHAT A BRAND CAN MOVE.
// Four surfaces read them — the header lockup, the active tab underline, the
// selected metric chip and one accent rule — and nothing else. If a fifth
// surface starts reading `--portal-accent`, that is the moment to check it is
// not quietly becoming a skin, and to check it is not a DATA colour (chart
// series, coverage fills and delta up/down are fixed across every brand; see
// dataColours.test.ts for why Snap's red must never reach a chart).

/** The variable names, so the components and the tests share one vocabulary. */
export const PORTAL_THEME_VARS = {
  accent: '--portal-accent',
  accentDark: '--portal-accent-dark',
  ink: '--portal-ink',
  gradient: '--portal-gradient',
  /**
   * ⚠️ The ink that goes ON the accent. DERIVED, not supplied by the brand —
   * see `readableOn`.
   */
  accentForeground: '--portal-accent-foreground',
  fontSans: '--font-sans',
  fontDisplay: '--font-display',
  fontMono: '--font-mono',
} as const;

/**
 * Inline style for the portal root.
 *
 * ⚠️ A NULL THEME EMITS NOTHING — not a set of kyck-coloured variables. The
 * stylesheet's own defaults then apply, which means "unthemed" and "themed with
 * kyck's values" are the same rendered result reached by the same path, rather
 * than two code paths that could drift.
 *
 * ⚠️ An ABSENT optional field is likewise omitted rather than defaulted here.
 * A brand with no `gradient` must fall through to whatever the stylesheet says,
 * and a brand with no `fontMono` must keep Geist Mono — emitting `undefined`
 * into a custom property would set it to the empty string, which is NOT the
 * same as leaving it unset (the fallback in `var(--x, fallback)` would not fire).
 */
export function portalThemeStyle(theme: PortalBrandTheme | null): CSSProperties {
  if (!theme) return {};
  const vars: Record<string, string> = {
    [PORTAL_THEME_VARS.accent]: theme.accent,
    [PORTAL_THEME_VARS.accentForeground]: readableOn(theme.accent),
    [PORTAL_THEME_VARS.ink]: theme.ink,
    // ⚠️ Falls back to the accent rather than to a computed darker shade:
    // darkening a brand colour by arithmetic produces a colour the brand never
    // approved, and using the accent twice is visibly fine.
    [PORTAL_THEME_VARS.accentDark]: theme.accentDark ?? theme.accent,
  };
  if (theme.gradient) vars[PORTAL_THEME_VARS.gradient] = theme.gradient;
  if (theme.fontBody) vars[PORTAL_THEME_VARS.fontSans] = theme.fontBody;
  // ⚠️ Display falls back to BODY, not to Geist: a brand that names only one
  // face means both, and dropping to Geist for headings would mix two
  // typefaces on a page that asked for one.
  const display = theme.fontDisplay ?? theme.fontBody;
  if (display) vars[PORTAL_THEME_VARS.fontDisplay] = display;
  if (theme.fontMono) vars[PORTAL_THEME_VARS.fontMono] = theme.fontMono;
  return vars as CSSProperties;
}

/**
 * Black or white, whichever is legible on `background`.
 *
 * ⚠️⚠️ THIS IS A LEGIBILITY DECISION, NOT A BRAND ONE, which is why deriving it
 * is legitimate where deriving a brand COLOUR is not. The selected metric chip
 * puts 12px text on the accent, and the two brands land on opposite sides:
 *
 *   Snap    #C4161C + white ≈ 5.9:1 — fine.
 *   Fitstop #F15A22 + white ≈ 3.4:1 — BELOW AA for normal text, and these chips
 *           are text-xs so they do not qualify as large text. With black it is
 *           ≈ 6.2:1.
 *
 * Hardcoding white would therefore have shipped a control a client cannot
 * comfortably read, on the one brand whose accent is bright. Neither brand
 * specifies a foreground for its accent, so there is nothing to override.
 *
 * ⚠️ Uses the real WCAG relative-luminance formula rather than a YIQ
 * approximation: the approximations disagree with WCAG exactly in the
 * mid-brightness band that orange sits in, which is the case that matters here.
 *
 * An unparseable colour returns white — the previous behaviour, so a malformed
 * theme degrades to what it did before rather than to black text on black.
 */
export function readableOn(background: string): '#ffffff' | '#000000' {
  const rgb = parseHex(background);
  if (!rgb) return '#ffffff';
  const lum = relativeLuminance(rgb);
  // Contrast against white vs against black; pick the better.
  const onWhite = 1.05 / (lum + 0.05);
  const onBlack = (lum + 0.05) / 0.05;
  return onBlack > onWhite ? '#000000' : '#ffffff';
}

function parseHex(hex: string): [number, number, number] | null {
  const m = /^#([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1] as string, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function relativeLuminance([r, g, b]: [number, number, number]): number {
  const channel = (v: number): number => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}
